/**
 * CodeFormModal — create a CodeReferral for a médecin (POST /referral/codes). The médecin picker is
 * limited to approuve-link médecins (the server 409s otherwise; we pre-filter for a clean UX). Optional
 * per-code % override + expiry. Gated by referral.code.manage.
 *
 * When opened from a row, `lockedMedecinId` pins the médecin (the picker is shown read-only).
 */
import { useEffect, useId, useState } from "react";
import { Button, FormField, Input, Modal, Select, useToast } from "@/ui";
import { useT } from "@/app";
import { useCreateCode, type CreateCodeVars } from "../api";
import type { MedecinCard } from "../types";
import { isLinkLive, medecinOptions, parsePct } from "../lib";

export interface CodeFormModalProps {
  open: boolean;
  labId: string | null;
  cards: ReadonlyArray<MedecinCard>;
  /** Pin to one médecin (opened from a row); when set the picker is disabled. */
  lockedMedecinId?: string | null;
  disabled?: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

export function CodeFormModal({
  open,
  labId,
  cards,
  lockedMedecinId,
  disabled,
  onClose,
  onCreated,
}: CodeFormModalProps) {
  const { t } = useT();
  const toast = useToast();
  const create = useCreateCode(labId);
  const formId = useId();

  // Only approuve-link médecins can receive a code.
  const eligible = cards.filter((c) => isLinkLive(c.lien_statut));
  const options = medecinOptions(eligible);

  const [medecinId, setMedecinId] = useState("");
  const [code, setCode] = useState("");
  const [libelle, setLibelle] = useState("");
  const [remise, setRemise] = useState("");
  const [expire, setExpire] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setMedecinId(lockedMedecinId ?? "");
      setCode("");
      setLibelle("");
      setRemise("");
      setExpire("");
      setTouched(false);
    }
  }, [open, lockedMedecinId]);

  const pct = parsePct(remise);
  const medecinError =
    touched && medecinId === "" ? t("referral.field.required") : undefined;
  const codeError =
    touched && code.trim().length < 2 ? t("referral.field.required") : undefined;
  const remiseError = touched && pct === null ? t("referral.field.pctInvalid") : undefined;
  const canSubmit =
    !disabled && medecinId !== "" && code.trim().length >= 2 && pct !== null && !create.isPending;

  const submit = () => {
    setTouched(true);
    if (!canSubmit) return;
    const vars: CreateCodeVars = { medecin_id: medecinId, code: code.trim() };
    if (libelle.trim()) vars.libelle = libelle.trim();
    if (pct !== undefined) vars.remise_pct = pct;
    if (expire) vars.expire_at = new Date(expire).toISOString();
    create.mutate(vars, {
      onSuccess: (res) => {
        toast.success(t("referral.code.success", { code: res.code }));
        onCreated?.();
        onClose();
      },
      onError: (err) => toast.error(err.message, t("referral.code.title")),
    });
  };

  const lockedCard = lockedMedecinId
    ? cards.find((c) => c.medecin_id === lockedMedecinId)
    : undefined;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("referral.code.title")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("referral.action.cancel")}
          </Button>
          <Button
            variant="primary"
            form={formId}
            type="submit"
            loading={create.isPending}
            disabled={!canSubmit}
          >
            {t("referral.code.submit")}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}
      >
        <FormField
          label={t("referral.code.medecin")}
          required
          hint={t("referral.code.medecin.hint")}
          error={medecinError}
        >
          {(p) =>
            lockedMedecinId && lockedCard ? (
              <Input {...p} value={`${lockedCard.nom} — ${lockedCard.novera_email}`} readOnly />
            ) : (
              <Select
                {...p}
                value={medecinId}
                onChange={(e) => setMedecinId(e.target.value)}
                placeholder={t("referral.medecin.pick.placeholder")}
                options={options}
                invalid={!!medecinError}
              />
            )
          }
        </FormField>
        <FormField
          label={t("referral.code.code")}
          required
          hint={t("referral.code.code.hint")}
          error={codeError}
        >
          {(p) => (
            <Input
              {...p}
              mono
              className="nv-ltr-island"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus={!!lockedMedecinId}
              invalid={!!codeError}
            />
          )}
        </FormField>
        <FormField label={t("referral.code.libelle")}>
          {(p) => <Input {...p} value={libelle} onChange={(e) => setLibelle(e.target.value)} />}
        </FormField>
        <FormField
          label={t("referral.code.remise")}
          hint={t("referral.code.remise.hint")}
          error={remiseError}
        >
          {(p) => (
            <Input
              {...p}
              mono
              inputMode="decimal"
              value={remise}
              onChange={(e) => setRemise(e.target.value)}
              invalid={!!remiseError}
            />
          )}
        </FormField>
        <FormField label={t("referral.code.expire")}>
          {(p) => (
            <Input
              {...p}
              type="date"
              className="nv-ltr-island"
              value={expire}
              onChange={(e) => setExpire(e.target.value)}
            />
          )}
        </FormField>
      </form>
    </Modal>
  );
}
